import { Link } from 'react-router-dom';
import { HelpCircle, ArrowLeft } from 'lucide-react';

// Faithful representation of the AVZ Wholesale FAQ document.
const FAQS = [
  {
    q: 'How do I place an order?',
    a: 'Place your order on the website just like you would any regular online shopping. You will see the active order window. If applicable, complete payment and send POP to secure your order.',
  },
  {
    q: 'When is payment required?',
    a: 'Payment is required upfront, unless the order needs reviewing before it can be confirmed which is the case with some suppliers due to MOQ requirements. Orders are only processed once payment is received.',
  },
  {
    q: 'Can I change or cancel my order?',
    a: 'No—once orders are submitted to suppliers, they cannot be changed or cancelled. You can cancel an order while the order window is still open, but not after it has closed.',
  },
  {
    q: 'How long will my order take?',
    a: 'Timelines vary by supplier and order cycle. Estimated lead times are communicated with each order but you can work on 1-2 weeks after the order window has closed.',
  },
  {
    q: 'What if I collect late?',
    a: 'Late collection may affect product quality and there is always a chance that something might go missing. AVZ Wholesale cannot guarantee condition of goods collected outside the advised timeframe.',
  },
  {
    q: 'What if something is out of stock?',
    a: 'If an item is unavailable, a refund will be issued.',
  },
  {
    q: 'Do you offer refunds or returns?',
    a: 'Only for damaged or incorrect items and only if the supplier agrees to a refund. Issues must be reported within 24 hours of collection with photos.',
  },
  {
    q: 'Are there additional fees?',
    a: 'The prices you see include the admin fee and you will see the R15 transport fee when you order. Some suppliers charge courier fee according to the weight and size of our order and is often between 5-15% of your order value.',
  },
  {
    q: 'How will I receive updates?',
    a: 'All order updates are shared via the website and direct communication (email/WhatsApp where applicable).',
  },
];

export default function FAQPage() {
  return (
    <div data-testid="faq-page" className="bg-background min-h-screen">
      {/* Header */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 font-body text-xs text-foreground/50 hover:text-teal transition-colors mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-xl bg-teal/10">
              <HelpCircle className="h-6 w-6 text-teal" strokeWidth={1.75} />
            </div>
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold">
              AVZ Wholesale
            </p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-foreground tracking-tight mb-3">
            Frequently Asked Questions
          </h1>
          <p className="font-body text-sm text-foreground/55">Everything you need to know about ordering with AVZ Wholesale.</p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="space-y-6 sm:space-y-7">
          {FAQS.map((faq, idx) => (
            <div
              key={idx}
              data-testid={`faq-item-${idx}`}
              className="rounded-2xl bg-card border border-border p-5 sm:p-6"
            >
              <h2 className="font-heading text-lg sm:text-xl font-medium text-foreground mb-2.5 flex items-baseline gap-3">
                <span className="font-body text-sm font-bold text-gold">{String(idx + 1).padStart(2, '0')}</span>
                {faq.q}
              </h2>
              <p className="font-body text-sm sm:text-base text-foreground/70 leading-relaxed pl-0 sm:pl-9">
                {faq.a}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-card border border-border p-6 sm:p-8 text-center">
          <p className="font-body text-sm text-foreground/60 leading-relaxed">
            Still have a question? Reach out via your usual WhatsApp or email channel and we&apos;ll be happy to help.
          </p>
          <Link to="/shop">
            <span className="inline-block mt-4 font-body text-sm font-semibold text-teal hover:text-teal/80 transition-colors">
              Continue shopping →
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
